import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const ImageUploader = ({ 
  onImageUploaded, 
  currentImage = null, 
  label = 'Carica Immagine',
  bucketName = 'images'
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage);
  const fileInputRef = useRef(null);

  // Update preview when currentImage prop changes
  React.useEffect(() => {
    setPreview(currentImage);
  }, [currentImage]);

  const validateFile = (file) => {
    // Check file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      toast.error('Formato file non valido. Usa JPG, PNG o WebP');
      return false;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File troppo grande. Massimo 5MB');
      return false;
    }

    return true;
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!validateFile(file)) {
      event.target.value = '';
      return;
    }

    setUploading(true);

    try {
      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `${bucketName}/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('rarifex-uploads')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Supabase upload error:', error);
        throw error;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('rarifex-uploads')
        .getPublicUrl(filePath);

      setPreview(publicUrl);
      onImageUploaded(publicUrl);
      toast.success('Immagine caricata con successo!');
    } catch (error) {
      console.error('Upload error:', error);
      
      // Fallback: use FileReader for preview (base64)
      // This works locally but base64 shouldn't be stored in database
      toast.info('Uso salvataggio locale. Per produzione configura Supabase Storage.');
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setPreview(base64String);
        onImageUploaded(base64String);
      };
      reader.readAsDataURL(file);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onImageUploaded('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <Label>{label}</Label>
      
      {preview ? (
        <div className="relative">
          <img 
            src={preview} 
            alt="Preview" 
            className="w-full h-48 object-cover rounded-sm border border-slate-200"
          />
          <Button
            type="button"
            size="sm"
            variant="destructive"
            className="absolute top-2 right-2"
            onClick={handleRemove}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div className="border-2 border-dashed border-slate-300 rounded-sm p-8 text-center hover:border-[#008080] transition-colors">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
            id="image-upload"
          />
          <label 
            htmlFor="image-upload" 
            className="cursor-pointer flex flex-col items-center"
          >
            <Upload className="w-10 h-10 text-slate-400 mb-3" />
            <p className="text-sm font-medium text-slate-600 mb-1">
              Clicca per caricare un'immagine
            </p>
            <p className="text-xs text-slate-500">
              JPG, PNG o WebP (max 5MB)
            </p>
          </label>
        </div>
      )}

      {uploading && (
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-[#008080]"></div>
          <p className="text-sm text-slate-600 mt-2">Caricamento...</p>
        </div>
      )}
    </div>
  );
};
